export const PREPARATION_FAMILIES = Object.freeze({
  MILITARY: 'military',
  COMMERCIAL: 'commercial'
});

export const PREPARATION_FAMILY_OPTIONS = Object.freeze([
  {
    id: PREPARATION_FAMILIES.MILITARY,
    label: 'Military Aircrew',
    onboardingLabel: 'Military aircrew selection',
    description: 'RAF, CFAST, ADF and other military aviation aptitude preparation.'
  },
  {
    id: PREPARATION_FAMILIES.COMMERCIAL,
    label: 'Commercial Pilot',
    onboardingLabel: 'Commercial pilot selection',
    description: 'Airline, cadet-pilot and flight-school aptitude preparation.'
  }
]);

export const COMMERCIAL_PROFILE_IDS = Object.freeze([
  'commercial-general',
  'adapt',
  'compass',
  'aon-cut-e',
  'pilapt'
]);

export const COMMERCIAL_PROFILES = Object.freeze({
  'commercial-general': Object.freeze({
    id: 'commercial-general',
    label: 'General Commercial Pilot Aptitude',
    shortLabel: 'General Commercial',
    availability: 'available',
    description: 'Broad, ability-based preparation for airline, cadet-pilot and flight-school selection.',
    disclaimer: 'An Academy-defined preparation profile, not an official airline or assessment-provider specification.'
  }),
  adapt: Object.freeze({
    id: 'adapt',
    label: 'ADAPT',
    shortLabel: 'ADAPT',
    availability: 'available',
    description: 'A computerised pilot screening system developed by Symbiotics, used in ab-initio cadet and airline selection. Publicly documented components include cognitive reasoning, working memory, perceptual speed and accuracy, spatial reasoning, multitasking and coordination tasks, alongside a personality questionnaire.',
    disclaimer: 'CBAT Academy is independent and does not reproduce proprietary ADAPT assessments.'
  }),
  compass: Object.freeze({
    id: 'compass',
    label: 'COMPASS',
    shortLabel: 'COMPASS',
    availability: 'available',
    description: 'A computerised pilot aptitude screening system used by a number of European airlines and flight schools. Publicly documented components include psychomotor tracking, memory and multitasking exercises alongside broader numerical, spatial and reasoning tests.',
    disclaimer: 'CBAT Academy is independent and does not reproduce proprietary COMPASS assessments.'
  }),
  'aon-cut-e': Object.freeze({
    id: 'aon-cut-e',
    label: 'Aon / cut-e',
    shortLabel: 'Aon / cut-e',
    availability: 'available',
    description: "A widely used aviation psychometric battery, delivered through Aon's Aviation Suite (formerly cut-e), used across many major airlines' cadet and direct-entry pilot selection. Publicly documented components include spatial awareness, short-term memory, multitasking, hand-eye coordination, arithmetic and logical-reasoning exercises.",
    disclaimer: 'CBAT Academy is independent and does not reproduce proprietary Aon or cut-e assessments.'
  }),
  pilapt: Object.freeze({
    id: 'pilapt',
    label: 'PILAPT',
    shortLabel: 'PILAPT',
    availability: 'available',
    description: 'A British-developed computerised pilot aptitude battery used by flight schools and some airlines, particularly in the UK and Europe. It is best known for a psychomotor tracking exercise, alongside spatial reasoning, numerical ability, pattern recognition and workload-management tasks.',
    disclaimer: 'CBAT Academy is independent and does not reproduce proprietary PILAPT assessments.'
  })
});

export const CANDIDATE_STAGES = Object.freeze([
  { id: 'cadet-ab-initio', label: 'Cadet / Ab-initio' },
  { id: 'flight-school-selection', label: 'Flight-school selection' },
  { id: 'first-airline-role', label: 'First airline role' },
  { id: 'experienced-direct-entry', label: 'Experienced / Direct entry' }
]);

export const SESSION_LENGTH_OPTIONS = Object.freeze([15, 30, 45, 60]);

export const REST_DAY_OPTIONS = Object.freeze([
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday'
]);

export const DEFAULT_PREPARATION_PREFERENCES = Object.freeze({
  military_enabled: true,
  commercial_enabled: false,
  active_family: PREPARATION_FAMILIES.MILITARY,
  military_profile: 'general',
  military_role: null,
  commercial_profile: 'commercial-general',
  candidate_stage: null,
  assessment_date: null,
  training_days_per_week: 4,
  typical_session_minutes: 30,
  preferred_rest_day: null,
  weekly_summary_opt_in: false,
  training_reminders_opt_in: false,
  assessment_prep_emails_opt_in: false,
  assessment_results_followup_opt_in: false,
  aggregate_analysis_opt_in: false
});

// One "Receive emails" choice covers every optional email: the weekly
// summary, the assessment countdown and the post-assessment check-in. The
// assessment pair is what the dashboard's "My assessment" card switches on its
// own, since that is where a date is set.
export const EMAIL_OPT_IN_FIELDS = Object.freeze([
  'weekly_summary_opt_in',
  'assessment_prep_emails_opt_in',
  'assessment_results_followup_opt_in'
]);
export const ASSESSMENT_EMAIL_OPT_IN_FIELDS = Object.freeze([
  'assessment_prep_emails_opt_in',
  'assessment_results_followup_opt_in'
]);

const fieldsSetTo = (fields, value) => Object.fromEntries(fields.map((field) => [field, value]));
export const emailOptInFields = (value) => fieldsSetTo(EMAIL_OPT_IN_FIELDS, value);
export const assessmentEmailOptInFields = (value) => fieldsSetTo(ASSESSMENT_EMAIL_OPT_IN_FIELDS, value);
export const receivesEmails = (preferences) => EMAIL_OPT_IN_FIELDS.some((field) => Boolean(preferences?.[field]));
export const receivesAssessmentEmails = (preferences) => (
  ASSESSMENT_EMAIL_OPT_IN_FIELDS.some((field) => Boolean(preferences?.[field]))
);

export const normalizePreparationPreferences = (preferences = {}, legacyProfile = {}) => {
  const next = {
    ...DEFAULT_PREPARATION_PREFERENCES,
    military_profile: legacyProfile.service_profile || DEFAULT_PREPARATION_PREFERENCES.military_profile,
    military_role: legacyProfile.target_role || null,
    ...preferences
  };

  if (!COMMERCIAL_PROFILES[next.commercial_profile]) {
    next.commercial_profile = DEFAULT_PREPARATION_PREFERENCES.commercial_profile;
  }

  // Exactly one family is ever enabled -- a candidate prepares for one
  // track at a time. A stale pre-enforcement cache (or a hand-crafted call)
  // could still carry an impossible both-true/both-false state; collapse it
  // here rather than round-tripping it to the backend and having it
  // rejected.
  if (next.military_enabled && next.commercial_enabled) {
    if (next.active_family === PREPARATION_FAMILIES.COMMERCIAL) {
      next.military_enabled = false;
    } else {
      next.commercial_enabled = false;
      next.active_family = PREPARATION_FAMILIES.MILITARY;
    }
  }
  if (!next.military_enabled && !next.commercial_enabled) {
    next.military_enabled = true;
    next.active_family = PREPARATION_FAMILIES.MILITARY;
  }
  if (!next[`${next.active_family}_enabled`]) {
    next.active_family = next.commercial_enabled
      ? PREPARATION_FAMILIES.COMMERCIAL
      : PREPARATION_FAMILIES.MILITARY;
  }
  return next;
};

export const getCommercialProfile = (profileId) => (
  COMMERCIAL_PROFILES[profileId] || COMMERCIAL_PROFILES['commercial-general']
);

export const getCandidateStage = (stageId) => (
  CANDIDATE_STAGES.find((stage) => stage.id === stageId) || null
);

export const getActivePreparationFamily = (profile = {}) => (
  normalizePreparationPreferences(profile.preparation_preferences, profile).active_family
);
